<script setup>
import Icon from "../../../Common/Icon.vue";
import { computed } from "vue";
import { ref } from "vue";

const emit = defineEmits(["filterChange"]);

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
  children: {
    type: Array,
    required: true,
  },
  id: {
    type: Number,
    required: true,
  },
});

// Open
const isOpen = ref(false);

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

// Children
const hasChildren = computed(() => {
  return props.children.length > 0;
});

const childIds = computed(() => {
  if (!hasChildren.value) {
    return [];
  }

  const ids = [];

  function addChildIds(option) {
    ids.push(option.id);

    if (option.children.length > 0) {
      for (const c in option.children) {
        addChildIds(option.children[c]);
      }
    }
  }

  for (const c in props.children) {
    addChildIds(props.children[c]);
  }

  return ids;
});

const activeChildren = computed(() => {
  if (!hasChildren.value) {
    return [];
  }

  return Object.values(props.modelValue).filter((option) => option.active && childIds.value.includes(option.id));
});

const activeChildrenAmount = computed(() => {
  return activeChildren.value.length;
});
</script>

<template>
  <div>
    <div class="flex items-center gap-x-4">
      <input
        type="checkbox"
        :name="`${props.name}-option-${props.id}`"
        :id="`${props.name}-option-${props.id}`"
        class="accent-primary size-4"
        :value="props.id"
        @change="emit('filterChange', props.id)"
        v-model="props.modelValue[id].active"
      />
      <label :for="`${props.name}-option-${props.id}`">
        <span v-text="props.modelValue[id].name"></span>
        <span v-if="activeChildrenAmount > 0" v-text="activeChildrenAmount" class="text-dark/50 ml-2"> </span>
      </label>

      <button
        v-if="hasChildren"
        type="button"
        @click="toggleOpen()"
        class="grow shrink-0 p-2 flex items-center justify-end"
      >
        <Icon :class="isOpen ? 'rotate-180' : ''" name="caret-down" class="size-3 transition-transform" />
      </button>
    </div>

    <div
      v-if="hasChildren"
      :class="isOpen ? 'grid-rows-accordion-open' : 'grid-rows-accordion-close'"
      class="grid transition-accordion"
    >
      <div class="overflow-hidden">
        <div class="space-y-4 pl-4 pt-4">
          <HierarchyRow
            :name="props.name"
            v-for="child in props.children"
            :key="child.id"
            :id="child.id"
            :children="child.children"
            v-model="props.modelValue"
            @filterChange="emit('filterChange', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
